import { NgClass } from '@angular/common'
import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, computed, effect, input, output, signal } from '@angular/core'
import { PERSONA_ARCHIVE_MENU_KEY } from '@app/v2/event/components/event-personas/event-persona-menu-options.constants'
import { Persona } from '@app/v2/personas/types/persona.type'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { NgxTippyModule } from 'ngx-tippy-wrapper'

@Component({
  selector: 'app-event-persona-card',
  imports: [DropdownComponent, NgxTippyModule, NgClass],
  templateUrl: './event-persona-card.component.html',
  styleUrl: './event-persona-card.component.scss'
})
export class EventPersonaCardComponent implements AfterViewInit, OnDestroy {
  public persona = input.required<Persona>()
  public personaMenuOptions = input.required<DropdownOption[]>()
  public personaMenuDropdownHeight = computed(() => (this.personaMenuOptions().some((option) => option.key === PERSONA_ARCHIVE_MENU_KEY) ? 293 : 247))
  public readonly readonly = input(false)
  public readonly optionClicked = output<DropdownOption>()
  public readonly isNameTruncated = signal(false)
  public readonly isDropdownOpen = signal(false)
  @ViewChild('personaNameRef') private _personaNameRef?: ElementRef<HTMLElement>
  private _resizeObserver?: ResizeObserver

  constructor() {
    effect(() => {
      this.persona().name
      requestAnimationFrame(() => this._updateTruncationState())
    })
  }

  ngAfterViewInit(): void {
    const element = this._personaNameRef?.nativeElement
    if (!element) return
    this._resizeObserver = new ResizeObserver(() => this._updateTruncationState())
    this._resizeObserver.observe(element)
    this._updateTruncationState()
  }

  /**
   * Format the registration count for the persona
   * @returns {string} - The formatted registration count
   */
  public formatRegistrationCount(): string {
    return (this.persona().registration_count ?? 0).toLocaleString()
  }

  /**
   * Reuses the same parent flow as "View registrants" dropdown option.
   * @param event {MouseEvent} - The mouse event.
   */
  public onRegistrationCountClick(event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this.optionClicked.emit({ key: 'view_registrants', value: 'View registrants' })
  }

  public onDropdownOpened(): void {
    this.isDropdownOpen.set(true)
  }

  public onDropdownClosed(): void {
    this.isDropdownOpen.set(false)
  }

  /**
   * Updates the truncation state of the persona name.
   */
  private _updateTruncationState(): void {
    const element = this._personaNameRef?.nativeElement
    if (!element) return
    this.isNameTruncated.set(element.scrollHeight > element.clientHeight + 1 || element.scrollWidth > element.clientWidth + 1)
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect()
  }
}
